import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  useTheme,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Send as SendIcon,
  Edit as EditIcon,
  PersonOff as BlockIcon,
  SkipNext as NextIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import CookieConsent from 'react-cookie-consent';
import { ThemeProvider, useColorMode } from './theme/ThemeProvider';
import TermsAndConditions from './components/TermsAndConditions';
import Notification from './components/Notification';
import LandingPage from './components/LandingPage';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
const SOCKET_SERVER = process.env.REACT_APP_SOCKET_SERVER || 'http://localhost:5000';

function LoadingAnimation({ status }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {status}
      </Typography>
    </Box>
  );
}

function AppContent() {
  const [isConnected, setIsConnected] = useState(false);
  const [nickname, setNickname] = useState(() => {
    // Initialize nickname from localStorage if it exists
    const savedNickname = localStorage.getItem('chatSession');
    return savedNickname || '';
  });
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isJoined, setIsJoined] = useState(() => {
    // Initialize isJoined based on whether we have a nickname
    return !!localStorage.getItem('chatSession');
  });
  const [error, setError] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [isLoading, setIsLoading] = useState(() => {
    // Initialize loading state based on whether we have a nickname
    return !!localStorage.getItem('chatSession');
  });
  const [currentPartnerId, setCurrentPartnerId] = useState(null);
  const [partnerNickname, setPartnerNickname] = useState(null);
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const [previousPartners, setPreviousPartners] = useState(() => {
    const storedPartners = localStorage.getItem('previousPartners');
    return new Set(storedPartners ? JSON.parse(storedPartners) : []);
  });
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [tempNickname, setTempNickname] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [showTerms, setShowTerms] = useState(() => {
    // Only show terms if they haven't been accepted before
    return localStorage.getItem('termsAccepted') !== 'true';
  });
  const [termsAccepted, setTermsAccepted] = useState(() => {
    return localStorage.getItem('termsAccepted') === 'true';
  });
  const theme = useTheme();
  const { toggleColorMode } = useColorMode();
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Add constants for validation
  const MAX_MESSAGE_LENGTH = 500;
  const MAX_NICKNAME_LENGTH = 20;
  const MIN_NICKNAME_LENGTH = 3;

  // Add refs for values that shouldn't trigger re-renders
  const messageRef = useRef(message);
  const nicknameRef = useRef(nickname);
  const currentPartnerIdRef = useRef(currentPartnerId);
  const partnerNicknameRef = useRef(partnerNickname);
  const previousPartnersRef = useRef(previousPartners);

  // Update refs when values change
  useEffect(() => {
    messageRef.current = message;
    nicknameRef.current = nickname;
    currentPartnerIdRef.current = currentPartnerId;
    partnerNicknameRef.current = partnerNickname;
    previousPartnersRef.current = previousPartners;
  }, [message, nickname, currentPartnerId, partnerNickname, previousPartners]);

  // Add connection state enum
  const ConnectionState = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    JOINING: 'joining',
    WAITING: 'waiting',
    PAIRED: 'paired'
  };

  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);

  // Update useEffect for connection handling
  useEffect(() => {
    const anonymousId = localStorage.getItem('anonymousId') || generateAnonymousId();
    localStorage.setItem('anonymousId', anonymousId);

    // Initialize socket connection
    console.log('Initializing socket connection...');
    setConnectionStatus('Connecting to server...');
    setConnectionState(ConnectionState.CONNECTING);
    
    socketRef.current = io(SOCKET_SERVER, {
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      autoConnect: true
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected successfully');
      setIsConnected(true);
      setConnectionState(ConnectionState.CONNECTED);
      setConnectionStatus('Connected to server');
      setError('');
      joinChat(nickname);
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setConnectionStatus('Failed to connect to chat server. Please try again.');
      setError('Unable to connect to chat server. Please check your connection and try again.');
      setIsConnected(false);
      setIsLoading(false);
      setIsJoined(false);
      setConnectionState(ConnectionState.DISCONNECTED);
      
      // Clear partner state on connection error
      setCurrentPartnerId(null);
      setPartnerNickname(null);
      setIsPartnerTyping(false);
    });

    socketRef.current.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
      setConnectionStatus('Disconnected from server. Attempting to reconnect...');
      setIsLoading(true);
      setConnectionState(ConnectionState.DISCONNECTED);
      
      // Clear partner state on disconnect
      setCurrentPartnerId(null);
      setPartnerNickname(null);
      setIsPartnerTyping(false);
      
      // Add system message about disconnection
      setMessages(prevMessages => [...prevMessages, {
        text: "Disconnected from server. Attempting to reconnect...",
        sender: 'System',
        timestamp: new Date().toISOString(),
        isSystem: true,
        isError: true
      }]);
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionState(ConnectionState.CONNECTED);
      setConnectionStatus('Reconnected to server');
      setError('');

      // Add system message about reconnection
      setMessages(prevMessages => [...prevMessages, {
        text: "Reconnected to server!",
        sender: 'System',
        timestamp: new Date().toISOString(),
        isSystem: true,
        isHighlighted: true
      }]);

      // If we were in a chat session, try to rejoin
      const existingSession = localStorage.getItem('chatSession');
      if (existingSession && isJoined) {
        console.log('Attempting to rejoin chat after reconnection');
        setConnectionState(ConnectionState.JOINING);
        joinChat(existingSession);
      }
    });

    socketRef.current.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
      setConnectionStatus('Failed to reconnect. Please refresh the page.');
      setError('Unable to reconnect to the chat server.');
      setIsConnected(false);
      setIsLoading(false);
      setIsJoined(false);
    });

    socketRef.current.on('reconnect_failed', () => {
      console.error('Socket reconnection failed after all attempts');
      setConnectionStatus('Failed to reconnect after multiple attempts. Please refresh the page.');
      setError('Unable to reconnect to the chat server after multiple attempts.');
      setIsConnected(false);
      setIsLoading(false);
      setIsJoined(false);
    });

    // Load previous partners from localStorage
    const storedPreviousPartners = localStorage.getItem('previousPartners');
    if (storedPreviousPartners) {
      setPreviousPartners(new Set(JSON.parse(storedPreviousPartners)));
    }

    // Add storage event listener to sync data across windows
    const handleStorageChange = (e) => {
      if (e.key === 'chatSession' && e.newValue) {
        setNickname(e.newValue);
        setIsJoined(true);
        setIsLoading(true);
      }
      if (e.key === 'previousPartners' && e.newValue) {
        setPreviousPartners(new Set(JSON.parse(e.newValue)));
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isJoined]); // Add isJoined as dependency

  // Update joinChat function
  const joinChat = (nick) => {
    const anonymousId = localStorage.getItem('anonymousId');
    const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
    const previousPartnersArray = Array.from(previousPartnersRef.current);
    
    setConnectionState(ConnectionState.JOINING);
    setConnectionStatus('Connected! Looking for a chat partner...');
    console.log('Joining chat with data:', {
      nickname: nick,
      anonymousId,
      blockedUsers,
      previousPartners: previousPartnersArray
    });
    
    // Clear any existing messages when joining new chat
    setMessages([]);
    setCurrentPartnerId(null);
    setPartnerNickname(null);
    setIsPartnerTyping(false);
    
    socketRef.current.emit('join', {
      nickname: nick,
      anonymousId,
      blockedUsers,
      previousPartners: previousPartnersArray
    });
    setIsLoading(true);
    localStorage.setItem('chatSession', nick);
  };

  // Save previous partners to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('previousPartners', JSON.stringify(Array.from(previousPartners)));
  }, [previousPartners]);

  const generateAnonymousId = () => {
    return 'id-' + Math.random().toString(36).substr(2, 9);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleTyping = () => {
    if (socketRef.current) {
      socketRef.current.emit('typing', true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('typing', false);
      }, 1000);
    }
  };

  // Update socket event handlers
  useEffect(() => {
    if (!socketRef.current) return;

    const handleWaiting = () => {
      console.log('Server: Added to waiting queue, waiting for partner...');
      setConnectionState(ConnectionState.WAITING);
      setConnectionStatus('Looking for an available chat partner...');
      setIsLoading(true);
      setCurrentPartnerId(null);
      setPartnerNickname(null);
      setIsPartnerTyping(false);

      // Add a system message about waiting
      setMessages(prevMessages => [...prevMessages, {
        text: "Waiting for a chat partner to connect...",
        sender: 'System',
        timestamp: new Date().toISOString(),
        isSystem: true,
        isHighlighted: true
      }]);
    };

    const handlePaired = (data) => {
      console.log('Server: Paired with partner:', data);
      if (!data.partnerId || !data.partnerNickname) {
        console.error('Missing partner data:', data);
        setConnectionStatus('Error in pairing. Looking for a new partner...');
        // Request new partner if data is invalid
        findNewPartner();
        return;
      }
      
      setConnectionState(ConnectionState.PAIRED);
      setIsJoined(true);
      setIsLoading(false);
      setError('');
      setCurrentPartnerId(data.partnerId);
      setPartnerNickname(data.partnerNickname);
      setConnectionStatus(`Connected with ${data.partnerNickname}`);
      localStorage.setItem('chatSession', nicknameRef.current);
      
      // Add system message for successful pairing
      setMessages(prevMessages => [...prevMessages, {
        text: `You are now chatting with ${data.partnerNickname}`,
        sender: 'System',
        timestamp: new Date().toISOString(),
        isSystem: true,
        isHighlighted: true
      }]);

      console.log('Successfully paired with:', {
        partnerId: data.partnerId,
        partnerNickname: data.partnerNickname
      });
    };

    const handlePartnerDisconnected = () => {
      console.log('Server: Partner disconnected:', currentPartnerIdRef.current);
      if (currentPartnerIdRef.current) {
        const updatedPartners = new Set([...previousPartnersRef.current, currentPartnerIdRef.current]);
        setPreviousPartners(updatedPartners);
        localStorage.setItem('previousPartners', JSON.stringify([...updatedPartners]));
        
        setMessages(prevMessages => [{
          text: `${partnerNicknameRef.current} has disconnected from the chat. Looking for a new partner...`,
          sender: 'System',
          timestamp: new Date().toISOString(),
          isSystem: true,
          isHighlighted: true
        }]);
        
        findNewPartner();
      }
    };

    // Helper function to find new partner
    const findNewPartner = () => {
      setConnectionState(ConnectionState.WAITING);
      setCurrentPartnerId(null);
      setPartnerNickname(null);
      setIsPartnerTyping(false);
      setIsLoading(true);
      setConnectionStatus('Looking for a new chat partner...');

      const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
      const previousPartnersArray = Array.from(previousPartnersRef.current);
      console.log('Previous Partners:', [...previousPartnersArray]);
      console.log('Server: Requesting new partner:', {
        blockedUsers,
        previousPartners: previousPartnersArray
      });
      socketRef.current.emit('findNewPartner', {
        blockedUsers,
        previousPartners: previousPartnersArray
      });
    };

    // Add handler for user count updates
    const handleUserCount = (data) => {
      console.log('Server: Active users count:', data.count);
      setUserCount(data.count);
    };

    // Add handler for typing status
    const handlePartnerTyping = (isTyping) => {
      if (!currentPartnerIdRef.current) return;
      console.log('Server: Partner typing status:', isTyping);
      setIsPartnerTyping(isTyping);
    };

    // Add handler for messages
    const handleMessage = (message) => {
      if (!currentPartnerIdRef.current) {
        console.warn('Received message but no partner is set');
        return;
      }
      console.log('Server: Received message:', message);
      setMessages((prevMessages) => [...prevMessages, {
        ...message,
        isMe: false
      }]);
    };

    // Add handler for message sent confirmation
    const handleMessageSent = (response) => {

      if (!currentPartnerIdRef.current) {
        console.warn('Message sent confirmation but no partner is set');
        return;
      }

      console.log('Server: Message sent status:', response);
      if (response.success) {
        setMessages(prevMessages => [...prevMessages, {
          text: response.text,
          sender: nicknameRef.current,
          timestamp: response.timestamp,
          isMe: true
        }]);  
      } else {
        setMessages(prevMessages => [...prevMessages, {
          text: response.error || "Failed to send message",
          sender: 'System',
          timestamp: new Date().toISOString(),
          isSystem: true,
          isError: true
        }]);
      }
    };

    // Add handler for server errors
    const handleError = (error) => {
      console.error('Server error:', error);
      setError(error.message);
      if (error.type === 'pairing') {
        setConnectionStatus('Error in pairing. Retrying...');
        // Retry finding partner after a short delay
        setTimeout(findNewPartner, 3000);
      }
    };

    console.log('Setting up socket event handlers');
    socketRef.current.on('waiting', handleWaiting);
    socketRef.current.on('paired', handlePaired);
    socketRef.current.on('partnerDisconnected', handlePartnerDisconnected);
    socketRef.current.on('message', handleMessage);
    socketRef.current.on('messageSent', handleMessageSent);
    socketRef.current.on('userCount', handleUserCount);
    socketRef.current.on('partnerTyping', handlePartnerTyping);
    socketRef.current.on('error', handleError);

    return () => {
      console.log('Cleaning up socket event handlers');
      if (socketRef.current) {
        socketRef.current.off('waiting', handleWaiting);
        socketRef.current.off('paired', handlePaired);
        socketRef.current.off('partnerDisconnected', handlePartnerDisconnected);
        socketRef.current.off('message', handleMessage);
        socketRef.current.off('messageSent', handleMessageSent);
        socketRef.current.off('userCount', handleUserCount);
        socketRef.current.off('partnerTyping', handlePartnerTyping);
        socketRef.current.off('error', handleError);
      }
    };
  }, [socketRef.current]); // Keep only socketRef.current as dependency

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isJoined) {
      if (!nickname.trim()) {
        setError('Please enter a nickname');
        return;
      }
      if (nickname.length < MIN_NICKNAME_LENGTH) {
        setError(`Nickname must be at least ${MIN_NICKNAME_LENGTH} characters`);
        return;
      }
      if (nickname.length > MAX_NICKNAME_LENGTH) {
        setError(`Nickname cannot exceed ${MAX_NICKNAME_LENGTH} characters`);
        return;
      }
      localStorage.setItem('chatSession', nickname);
      setIsJoined(true);
      setIsLoading(true);
      joinChat(nickname);
    } else if (message.trim() && !isLoading && currentPartnerId) {
      if (message.length > MAX_MESSAGE_LENGTH) {
        setMessages(prevMessages => [...prevMessages, {
          text: `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`,
          sender: 'System',
          timestamp: new Date().toISOString(),
          isSystem: true,
          isError: true
        }]);
        return;
       
      }
      socketRef.current.emit('sendMessage', {
        text: message,
        sender: nickname
      });
      // Clear message input immediately
      setMessage('');
    } else if (message.trim() && (!currentPartnerId || isLoading)) {
      // Show error message if trying to send without a partner
      setMessages(prevMessages => [...prevMessages, {
        text: "Can't send messages until you're connected to a partner",
        sender: 'System',
        timestamp: new Date().toISOString(),
        isSystem: true,
        isError: true
      }]);
      setMessage('');
    }
  };

  const handleUnpair = () => {
    if (!socketRef.current || !currentPartnerIdRef.current || isLoading) return;
    
    // Update both state and localStorage atomically
    const updatedPartners = new Set([...previousPartnersRef.current, currentPartnerIdRef.current]);
    setPreviousPartners(updatedPartners);
    localStorage.setItem('previousPartners', JSON.stringify([...updatedPartners]));
    
    // Clear current state
    setMessages([]);
    setCurrentPartnerId(null);
    setPartnerNickname(null);
    setIsPartnerTyping(false);
    setIsLoading(true);
    setConnectionStatus('Looking for a new chat partner...');
    console.log('Previous Partners:', [...updatedPartners]);
    // Emit findNewPartner event with updated previous partners
    socketRef.current.emit('findNewPartner', {
      blockedUsers: JSON.parse(localStorage.getItem('blockedUsers') || '[]'),
      previousPartners: [...updatedPartners]
    });
  };

  const handleBlock = () => {
    if (!socketRef.current || !currentPartnerIdRef.current) return;

    const blockedUsers = JSON.parse(localStorage.getItem('blockedUsers') || '[]');
    if (!blockedUsers.includes(currentPartnerIdRef.current)) {
      blockedUsers.push(currentPartnerIdRef.current);
      localStorage.setItem('blockedUsers', JSON.stringify(blockedUsers));
    }
    handleUnpair();
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    // Only emit typing status if we have a partner
    if (currentPartnerId && !isLoading) {
      handleTyping();
    }
  };

  const handleEditNickname = () => {
    setTempNickname(nickname);
    setIsEditingNickname(true);
  };

  const handleSaveNickname = () => {
    if (!tempNickname.trim()) {
      return;
    }
    if (tempNickname.length < MIN_NICKNAME_LENGTH) {
      setError(`Nickname must be at least ${MIN_NICKNAME_LENGTH} characters`);
      return;
    }
    if (tempNickname.length > MAX_NICKNAME_LENGTH) {
      setError(`Nickname cannot exceed ${MAX_NICKNAME_LENGTH} characters`);
      return;
    }
    setNickname(tempNickname);
    localStorage.setItem('chatSession', tempNickname);
    setIsEditingNickname(false);
    setError('');
  };

  // Add user count polling effect
  useEffect(() => {
    let interval;
    if (socketRef.current) {
      interval = setInterval(() => {
        socketRef.current.emit('getUserCount');
      }, 10000); // Poll every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [socketRef.current]);

  useEffect(() => {
    if (!termsAccepted) {
      setShowTerms(true);
    }
  }, [termsAccepted]);

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTerms(false);
    localStorage.setItem('termsAccepted', 'true');
  };

  const handleTermsDecline = () => {
    setShowTerms(false);
  };

  // Add socket event listener for warnings
  useEffect(() => {
    if (!socketRef.current) return;

    socketRef.current.on('warning', (data) => {
      setNotification({
        open: true,
        message: data.message,
        severity: 'warning'
      });
    });

    socketRef.current.on('error', (data) => {
      setNotification({
        open: true,
        message: data.message,
        severity: 'error'
      });
      setError(data.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off('warning');
        socketRef.current.off('error');
      }
    };
  }, [socketRef.current]);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <AppBar position="fixed" color="inherit" elevation={1} sx={{ backdropFilter: 'blur(8px)' }}>
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          <motion.img 
            src={'/someone2talklogo.png'} 
            alt="Someone2Talk" 
            style={{ height: 32, marginRight: 10 }}
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Someone2Talk
          </Typography>
          <IconButton onClick={toggleColorMode} color="inherit">
            <motion.div
              whileHover={{ rotate: 180 }}
              transition={{ duration: 0.3 }}
            >
              {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </motion.div>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 8, mb: 4 }}>
        <AnimatePresence mode="wait">
          {!isJoined ? (
            <LandingPage
              nickname={nickname}
              setNickname={setNickname}
              error={error}
              handleSubmit={handleSubmit}
              termsAccepted={termsAccepted}
            />
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 4, 
                  borderRadius: 4,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(0, 0, 0, 0.8)'
                    : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.1)'
                  }`,
                }}
              >
                <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isEditingNickname ? (
                        <>
                          <TextField
                            size="small"
                            value={tempNickname}
                            onChange={(e) => setTempNickname(e.target.value)}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveNickname}
                            startIcon={<SaveIcon />}
                            sx={{
                              bgcolor: theme.palette.success.main,
                              '&:hover': {
                                bgcolor: theme.palette.success.dark,
                              }
                            }}
                          >
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography variant="h6">
                            {nickname}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={handleEditNickname}
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: `${theme.palette.primary.main}10`
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '8px', padding: '4px 8px' }}>
                      Online Users: <CountUp end={userCount} duration={10}  />
                    </Typography>
                  </Box>

                  {connectionStatus && (
                    <Typography variant="body2" color="primary" align="center" sx={{ mb: 2 }}>
                      {connectionStatus}
                    </Typography>
                  )}

                  {partnerNickname && !isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Chatting with: {partnerNickname}
                    </Typography>
                  )}

                  {isPartnerTyping && !isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {partnerNickname} is typing...
                    </Typography>
                  )}

                  {isLoading ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <LoadingAnimation status={connectionStatus || 'Searching for a chat partner...'} />
                      </motion.div>
                    </Box>
                  ) : (
                    <>
                      <Box 
                        sx={{ 
                          flex: 1, 
                          overflowY: 'auto', 
                          mb: 2,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'rgba(0,0,0,0.1)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.primary.main,
                            borderRadius: '4px',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {messages.map((msg, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, x: msg.isMe ? 20 : -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <Box
                                sx={{
                                  display: 'flex', 
                                  alignItems: 'flex-end',
                                  gap: 1,
                                  flexDirection: msg.isMe ? 'row-reverse' : 'row',
                                  justifyContent: msg.isSystem ? 'center' : 'flex-start',
                                  mb: 1
                                }}
                              >
                                {!msg.isSystem && (
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      fontSize: '0.875rem',
                                      bgcolor: msg.isMe ? theme.palette.primary.main : theme.palette.grey[500],
                                      display: { xs: 'none', sm: 'flex' }
                                    }}
                                  >
                                    {msg.sender?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                )}
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: msg.isSystem ? 1 : '8px 12px',
                                    maxWidth: msg.isSystem ? '70%' : '80%',
                                    bgcolor: msg.isSystem
                                      ? msg.isError
                                        ? `${theme.palette.error.main}08`
                                        : msg.isHighlighted
                                        ? `${theme.palette.warning.main}08`
                                        : theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.03)'
                                        : 'rgba(0, 0, 0, 0.03)'
                                      : msg.isMe
                                      ? theme.palette.primary.main
                                      : theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: msg.isSystem ? '8px' : '16px',
                                    position: 'relative',
                                    '&::before': !msg.isSystem ? {
                                      content: '""',
                                      position: 'absolute',
                                      [msg.isMe ? 'right' : 'left']: '-4px',
                                      bottom: '8px',
                                      width: '8px',
                                      height: '8px',
                                      backgroundColor: 'inherit',
                                      transform: 'rotate(45deg)',
                                      zIndex: -1,
                                    } : undefined,
                                    boxShadow: 'none',
                                    border: msg.isSystem ? 'none' : `1px solid ${
                                      msg.isMe 
                                        ? theme.palette.primary.main 
                                        : theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.1)'
                                    }`,
                                  }}
                                >
                                  {!msg.isSystem ? (
                                    <Typography 
                                      variant="subtitle2" 
                                      sx={{ 
                                        color: msg.isMe 
                                          ? theme.palette.primary.contrastText
                                          : theme.palette.text.primary,
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        mb: 0.25,
                                        opacity: 0.8
                                      }}
                                    >
                                      {msg.sender}
                                    </Typography>
                                  ) : (
                                    <Typography 
                                      variant="caption"
                                      sx={{ 
                                        color: msg.isError
                                          ? theme.palette.error.main
                                          : msg.isHighlighted
                                          ? theme.palette.warning.main
                                          : theme.palette.text.secondary,
                                        fontStyle: 'italic',
                                        display: 'block',
                                        textAlign: 'center',
                                        fontSize: '0.7rem',
                                        opacity: 0.7
                                      }}
                                    >
                                      {msg.text}
                                    </Typography>
                                  )}
                                  {!msg.isSystem && (
                                    <Typography 
                                      variant="body1" 
                                      sx={{ 
                                        color: msg.isMe 
                                          ? theme.palette.primary.contrastText
                                          : theme.palette.text.primary,
                                        wordBreak: 'break-word',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {msg.text}
                                    </Typography>
                                  )}
                                  {!msg.isSystem && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: msg.isMe 
                                          ? `${theme.palette.primary.contrastText}80`
                                          : theme.palette.text.secondary,
                                        display: 'block',
                                        mt: 0.25,
                                        textAlign: 'right',
                                        fontSize: '0.65rem',
                                        opacity: 0.7
                                      }}
                                    >
                                      {new Date(msg.timestamp).toLocaleTimeString()}
                                    </Typography>
                                  )}
                                </Paper>
                              </Box>
                            </motion.div>
                          ))}
                          <div ref={messagesEndRef} />
                        </Box>
                      </Box>

                      <Box 
                        component="form" 
                        onSubmit={handleSubmit} 
                        sx={{ 
                          display: 'flex', 
                          gap: 1,
                          position: 'relative',
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          style={{ width: '100%' }}
                        >
                          <TextField
                            fullWidth
                            value={message}
                            onChange={handleMessageChange}
                            placeholder="Type your message..."
                            variant="outlined"
                            size="small"
                            multiline
                            maxRows={3}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(4px)',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                                },
                              }
                            }}
                          />
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            endIcon={<SendIcon />}
                            fullWidth={isMobile}
                            style={{ height: '40px' }}
                            sx={{
                              borderRadius: '12px',
                              px: { xs: 2, sm: 3 },
                              py: { xs: 1, sm: 'inherit' },
                              minWidth: { xs: '100%', sm: '100px' },
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              height: { xs: '40px', sm: 'auto' },
                            }}
                          >
                          </Button>
                        </motion.div>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <motion.div whileHover={{ scale: 1.02 }} style={{ width: '100%' }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleUnpair}
                            startIcon={<NextIcon />}
                            sx={{
                              borderRadius: '12px',
                            }}
                          >
                            Next Chat
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.02 }} style={{ width: '100%' }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            onClick={handleBlock}
                            startIcon={<BlockIcon />}
                            sx={{
                              borderRadius: '12px',
                            }}
                          >
                            Block & Next
                          </Button>
                        </motion.div>
                      </Box>
                    </>
                  )}
                </Box>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      <TermsAndConditions
        open={showTerms}
        onClose={handleTermsDecline}
        onAccept={handleTermsAccept}
      />

      <CookieConsent
        location="bottom"
        buttonText="Accept"
        cookieName="cookieConsent"
        style={{ background: theme.palette.grey[900] }}
        buttonStyle={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '8px',
          padding: '8px 16px',
        }}
      >
        This website uses cookies to enhance the user experience.
      </CookieConsent>

      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />
    </Container>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
